import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { Search } from 'lucide-react';
import _ from 'lodash';

interface Group {
    id: string;
    name: string;
    is_admin_group?: boolean;
}

interface RAGApplication {
    id: string;
    name: string;
    description?: string;
    groups?: Group[];
}

interface AssignRAGGroupsModalProps {
    show: boolean;
    application?: RAGApplication | null;
    groups: Group[];
    selectedGroupIds: string[];
    isUpdatingGroups: boolean;
    onHide: () => void;
    onConfirm: () => void;
    onGroupSelectionChange: (groupId: string, checked: boolean) => void;
}

const AssignRAGGroupsModal: React.FC<AssignRAGGroupsModalProps> = ({
                                                                       show,
                                                                       application,
                                                                       groups = [],
                                                                       selectedGroupIds = [],
                                                                       isUpdatingGroups,
                                                                       onHide,
                                                                       onConfirm,
                                                                       onGroupSelectionChange
                                                                   }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [newlySelectedGroups, setNewlySelectedGroups] = useState<Set<string>>(new Set());
    const [newlyUnselectedGroups, setNewlyUnselectedGroups] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (show) {
            setNewlySelectedGroups(new Set());
            setNewlyUnselectedGroups(new Set());
        }
    }, [show]);

    const { selectedGroups, availableGroups } = useMemo(() => {
        const selected = groups
            .filter(group =>
                group.is_admin_group || selectedGroupIds.includes(group.id)
            )
            .sort((a, b) => {
                if (a.is_admin_group && !b.is_admin_group) return -1;
                if (!a.is_admin_group && b.is_admin_group) return 1;
                return a.name.localeCompare(b.name);
            });

        const available = groups
            .filter(group =>
                !group.is_admin_group &&
                !selectedGroupIds.includes(group.id) &&
                group.name.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .sort((a, b) => a.name.localeCompare(b.name));

        return { selectedGroups: selected, availableGroups: available };
    }, [groups, selectedGroupIds, searchTerm]);

    const handleGroupSelection = (groupId: string, checked: boolean) => {
        if (checked) {
            setNewlySelectedGroups(prev => new Set(prev).add(groupId));
            setNewlyUnselectedGroups(prev => {
                const next = new Set(prev);
                next.delete(groupId);
                return next;
            });
        } else {
            // Only add to newlyUnselectedGroups if it wasn't newly selected
            if (!newlySelectedGroups.has(groupId)) {
                setNewlyUnselectedGroups(prev => new Set(prev).add(groupId));
            }
            setNewlySelectedGroups(prev => {
                const next = new Set(prev);
                next.delete(groupId);
                return next;
            });
        }
        onGroupSelectionChange(groupId, checked);
    };

    if (!application) return null;

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            backdrop="static"
            keyboard={false}
        >
            <Modal.Header closeButton className="border-bottom">
                <Modal.Title>Assign Groups to {application.name}</Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-4">
                {/* Selected Groups Section */}
                <div className="mb-4">
                    <h6 className="text-sm fw-medium mb-3">
                        Selected Groups
                        <span className="text-muted ms-2">({selectedGroups.length})</span>
                    </h6>
                    <div className="card border shadow-sm">
                        <div className="list-group list-group-flush" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {selectedGroups.length === 0 ? (
                                <div className="text-muted text-center p-3">
                                    No groups selected
                                </div>
                            ) : (
                                selectedGroups.map((group) => (
                                    <div
                                        key={group.id}
                                        className={`list-group-item d-flex align-items-center p-3 ${
                                            newlySelectedGroups.has(group.id) ? 'bg-success-subtle' : ''
                                        }`}
                                    >
                                        <Form.Check
                                            type="checkbox"
                                            id={`selected-group-${group.id}`}
                                            checked={true}
                                            onChange={() => {
                                                if (!group.is_admin_group) {
                                                    handleGroupSelection(group.id, false);
                                                }
                                            }}
                                            disabled={group.is_admin_group}
                                            label={
                                                <div className="ms-2 d-flex align-items-center">
                                                    <span className="text-sm">{group.name}</span>
                                                    {group.is_admin_group && (
                                                        <span className="badge bg-primary-subtle text-primary-emphasis ms-2">Admin</span>
                                                    )}
                                                    {newlySelectedGroups.has(group.id) && (
                                                        <span className="badge bg-success-subtle text-success-emphasis ms-2">New</span>
                                                    )}
                                                </div>
                                            }
                                        />
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* Search Input */}
                <div className="mb-3">
                    <div className="input-group">
                        <span className="input-group-text border">
                            <Search size={18} />
                        </span>
                        <input
                            type="text"
                            className="form-control border"
                            style={{height: '38px'}}
                            placeholder="Search available groups..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* Available Groups Section */}
                <div>
                    <h6 className="text-sm fw-medium mb-3">
                        Available Groups
                        <span className="text-muted ms-2">({availableGroups.length})</span>
                    </h6>
                    <div className="card border shadow-sm">
                        <div className="list-group list-group-flush" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {availableGroups.length === 0 ? (
                                <div className="text-muted text-center p-3">
                                    {searchTerm ? 'No matching groups found' : 'No available groups'}
                                </div>
                            ) : (
                                availableGroups.map((group) => (
                                    <div
                                        key={group.id}
                                        className={`list-group-item d-flex align-items-center p-3 ${
                                            newlyUnselectedGroups.has(group.id) ? 'bg-danger-subtle' : ''
                                        }`}
                                    >
                                        <Form.Check
                                            type="checkbox"
                                            id={`available-group-${group.id}`}
                                            checked={false}
                                            onChange={() => handleGroupSelection(group.id, true)}
                                            label={
                                                <div className="ms-2 d-flex align-items-center">
                                                    <span className="text-sm">{group.name}</span>
                                                    {newlyUnselectedGroups.has(group.id) && (
                                                        <span className="badge bg-danger-subtle text-danger-emphasis ms-2">Removed</span>
                                                    )}
                                                </div>
                                            }
                                        />
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer className="border-top">
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onHide}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={onConfirm}
                    disabled={isUpdatingGroups}
                >
                    {isUpdatingGroups ? 'Saving...' : 'Save Changes'}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default AssignRAGGroupsModal;